import { Fragment, ReactEventHandler } from "react"

import { ButtonBase, Stack } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import useMediaQuery from "@mui/material/useMediaQuery"
import usePagination from "@mui/material/usePagination"

import type { FC, FCWC } from ".."
import { SvgIcon } from ".."

type PaginationProps = {
  count?: number
  page?: number
  onPageChange?: (pageNumber: number) => void
}

const Pagination: FC<PaginationProps> = ({ count = 1, page = 1, onPageChange }) => {
  const currentPage = isNaN(Number(page)) ? 1 : Number(page)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const { items } = usePagination({
    count,
    page: currentPage,
    boundaryCount: isMobile ? 0 : 1,
    siblingCount: isMobile ? 1 : 2,
    onChange: (_, page) => {
      // @ts-ignore
      onPageChange(page)
    },
  })

  return (
    <Stack alignItems="center">
      <Stack
        component="nav"
        direction="row"
        alignItems="center"
        columnGap={1}
        aria-label="Pagination Navigation"
      >
        {items.map(({ page, type, selected, ...item }, index) => {
          let children = null

          if (type === "start-ellipsis" || type === "end-ellipsis") {
            children = <PaginationDotsBtn>⋯</PaginationDotsBtn>
          } else if (type === "page") {
            children = (
              <PaginationPageBtn selected={selected} aria-label={`Go to Page ${page}`} {...item}>
                {page}
              </PaginationPageBtn>
            )
          } else {
            children = <PaginationArrowBtn type={type} {...item} />
          }

          return <Fragment key={index}>{children}</Fragment>
        })}
      </Stack>
    </Stack>
  )
}
export default Pagination

type PaginationBtnProps = {
  onClick: ReactEventHandler<Element>
  disabled: boolean
}

/** A sub-component for the page button links in the pagination component */
type PageBtnProps = { selected: boolean }
const PaginationPageBtn: FCWC<PaginationBtnProps & PageBtnProps> = ({
  selected,
  children,
  ...props
}) => {
  return (
    <ButtonBase
      sx={(theme) => ({
        ...theme.typography["link.sm"],
        borderRadius: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 52,
        height: 52,
        ...(selected && {
          border: `1px solid ${theme.palette["neutral.200"].main}`,
        }),
        ...(!selected && {
          opacity: 0.6,
        }),
      })}
      {...props}
    >
      {children}
    </ButtonBase>
  )
}

/** A sub-component for the page button links in the pagination component */
const PaginationDotsBtn: FCWC = ({ children, ...props }) => {
  return (
    <ButtonBase
      sx={(theme) => ({
        ...theme.typography["link.sm"],
        borderRadius: 9999,
        display: { xs: "none", md: "flex" },
        alignItems: "center",
        justifyContent: "center",
        width: 52,
        height: 52,
        opacity: 0.6,
      })}
      disabled
      {...props}
    >
      {children}
    </ButtonBase>
  )
}

/** A sub-component for the arrow button links in the paginationcomponent */
type ArrowBtnProps = { type: "first" | "last" | "next" | "previous" }
const PaginationArrowBtn: FC<PaginationBtnProps & ArrowBtnProps> = ({ type, ...props }) => {
  return (
    <ButtonBase
      sx={{
        "width": 20,
        "height": 20,

        ...(type === "previous" && {
          mr: 10,
        }),

        ...(type === "next" && {
          ml: 10,
        }),

        "&:disabled": {
          svg: {
            fill: (theme) => theme.palette["neutral.300"].main,
          },
        },
      }}
      aria-label={`Go to the ${type} page`}
      {...props}
    >
      {type === "previous" && <SvgIcon variant="arrow" size={{ xs: 14, md: 20 }} />}
      {type === "next" && (
        <SvgIcon variant="arrow" size={{ xs: 14, md: 20 }} sx={{ transform: "rotate(180deg)" }} />
      )}
    </ButtonBase>
  )
}
